import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Project, ProjectForm, ProjectState, ApiResponse, PaginatedResponse } from '../../types'
import api from '../../services/api'

const initialState: ProjectState = {
  projects: [],
  currentProject: null,
  loading: false,
  error: null,
}

// Async thunks
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (params: { page?: number; limit?: number; search?: string; status?: string; type?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get<PaginatedResponse<Project>>('/projects', { params })
      return response.data.data.projects
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch projects')
    }
  }
)

export const fetchProject = createAsyncThunk(
  'projects/fetchProject',
  async (projectId: string, { rejectWithValue }) => {
    try {
      if (!projectId) {
        return rejectWithValue('Project ID is required')
      }
      const response = await api.get<ApiResponse<{ project: Project }>>(`/projects/${projectId}`)
      return response.data.data!.project
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch project')
    }
  }
)

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData: ProjectForm, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<{ project: Project }>>('/projects', projectData)
      return response.data.data!.project
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create project')
    }
  }
)

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ projectId, projectData }: { projectId: string; projectData: Partial<ProjectForm> }, { rejectWithValue }) => {
    try {
      const response = await api.put<ApiResponse<{ project: Project }>>(`/projects/${projectId}`, projectData)
      return response.data.data!.project
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update project')
    }
  }
)

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}`)
      return projectId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete project')
    }
  }
)

export const addProjectMember = createAsyncThunk(
  'projects/addProjectMember',
  async ({ projectId, userId, role }: { projectId: string; userId: string; role?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<{ project: Project }>>(`/projects/${projectId}/members`, { userId, role })
      return response.data.data!.project
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add member')
    }
  }
)

export const removeProjectMember = createAsyncThunk(
  'projects/removeProjectMember',
  async ({ projectId, userId }: { projectId: string; userId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}/members/${userId}`)
      return { projectId, userId }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove member')
    }
  }
)

export const fetchProjectStats = createAsyncThunk(
  'projects/fetchProjectStats',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<{ stats: any }>>(`/projects/${projectId}/stats`)
      return { projectId, stats: response.data.data!.stats }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch project stats')
    }
  }
)

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearCurrentProject: (state) => {
      state.currentProject = null
    },
    clearError: (state) => {
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    updateProjectInList: (state, action: PayloadAction<Project>) => {
      const index = state.projects.findIndex(p => p._id === action.payload._id)
      if (index !== -1) {
        state.projects[index] = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Projects
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false
        state.projects = action.payload
        state.error = null
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch Project
    builder
      .addCase(fetchProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.loading = false
        state.currentProject = action.payload
        state.error = null
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Create Project
    builder
      .addCase(createProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false
        state.projects.unshift(action.payload)
        state.error = null
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update Project
    builder
      .addCase(updateProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false
        const index = state.projects.findIndex(p => p._id === action.payload._id)
        if (index !== -1) {
          state.projects[index] = action.payload
        }
        if (state.currentProject && state.currentProject._id === action.payload._id) {
          state.currentProject = action.payload
        }
        state.error = null
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Delete Project
    builder
      .addCase(deleteProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false
        state.projects = state.projects.filter(p => p._id !== action.payload)
        if (state.currentProject && state.currentProject._id === action.payload) {
          state.currentProject = null
        }
        state.error = null
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Add Project Member
    builder
      .addCase(addProjectMember.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addProjectMember.fulfilled, (state, action) => {
        state.loading = false
        const index = state.projects.findIndex(p => p._id === action.payload._id)
        if (index !== -1) {
          state.projects[index] = action.payload
        }
        if (state.currentProject && state.currentProject._id === action.payload._id) {
          state.currentProject = action.payload
        }
        state.error = null
      })
      .addCase(addProjectMember.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Remove Project Member
    builder
      .addCase(removeProjectMember.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeProjectMember.fulfilled, (state, action) => {
        state.loading = false
        // Update projects list and current project
        if (state.currentProject && state.currentProject._id === action.payload.projectId) {
          state.currentProject.members = state.currentProject.members.filter(
            member => (typeof member.user === 'string' ? member.user : member.user._id) !== action.payload.userId
          )
        }
        state.error = null
      })
      .addCase(removeProjectMember.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch Project Stats
    builder
      .addCase(fetchProjectStats.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProjectStats.fulfilled, (state, action) => {
        state.loading = false
        // Add stats to current project if it matches
        if (state.currentProject && state.currentProject._id === action.payload.projectId) {
          // Note: stats are not part of the Project type, so we'll handle them separately if needed
          // state.currentProject = { ...state.currentProject, stats: action.payload.stats }
        }
        state.error = null
      })
      .addCase(fetchProjectStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { clearCurrentProject, clearError, setLoading, updateProjectInList } = projectSlice.actions
export default projectSlice.reducer
